// src/pages/settings/ConnectorsPage.tsx
//
// DEM-2120 (Conectores F2 — Marketplace UI) · /settings/connectors
//
// Marketplace de integrações no NÍVEL TENANT (ortogonal ao sistema), consumindo
// os contratos F1 (DEM-2119):
//   - GET    /v1/connectors/catalog            → catálogo global
//   - GET    /v1/connectors/connections        → conexões do tenant (revogadas fora)
//   - POST   /v1/connectors/connections        → conectar (credencial no vault)
//   - DELETE /v1/connectors/connections/:id    → revogar (revoked + soft-delete secret)
//
// Estrutura:
//   1. Grade de cards do catálogo com busca full-text (nome+descrição+categoria,
//      client-side sobre o payload — catálogo pequeno) + filtros combinados por
//      TIPO (API/MCP/ambos) e CATEGORIA.
//   2. Card: logo, nome, descrição, badges de modos (api_key/oauth2/mcp), status
//      do catálogo, contador informativo "N conectadas" (conexões do tenant nesse
//      conector, revogadas já excluídas pelo backend) e botão "Gerenciar contas"
//      (abre o painel de contas do conector) quando há 1+ conexão.
//   3. Modal "Conectar": escolha de modo (quando há +1), autenticação —
//      api_key (colar chave → grava no vault via secret_value; NUNCA renderizada
//      depois) e MCP (config do server) — e vínculo: conta do tenant
//      (business_id NULL) ou um business (dropdown).
//   4. Painel de detalhe (rota /:slug): todas as conexões do tenant naquele
//      conector, com display_name, vínculo (tenant ou business X), external_account,
//      status e ações reconectar (novo POST — re-auth) / revogar (DELETE).
//
// Segurança: a chave api_key é enviada como `secret_value` e o backend a cifra
// no vault (tenant_secrets). Esta UI NUNCA re-exibe a credencial após salvar.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  api,
  type ConnectorCatalogItem,
  type ConnectorConnection,
  type ConnectorAuthMode,
  type ConnectorCategory,
  type Business,
} from '@/lib/api';
import {
  Search,
  Blocks,
  Loader2,
  Plug,
  Trash2,
  RefreshCw,
  ArrowLeft,
  Link2,
  ExternalLink,
  Eye,
  AlertTriangle,
  DollarSign,
  Users,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
// DEM-2118: vocabulário de UI + leitores de external_account + ConnectModal
// agora vivem no módulo compartilhado (reusados pela sub-aba Redes Sociais).
import {
  CATEGORY_LABELS,
  MODE_LABELS,
  CATALOG_STATUS,
  CONN_STATUS,
  CONNECTABLE_MODES,
  readExternalAccount,
  readXMcpBudget,
  isReadOnly,
} from '@/components/connectors/connectors-ui';
import { ConnectModal } from '@/components/connectors/shared';

// ─── Vocabulário de UI ──────────────────────────────────────────────────────

/** Filtro de tipo: API (api_key/oauth2), MCP, ou ambos. */
type TypeFilter = 'all' | 'api' | 'mcp';

const CATEGORY_ORDER: ConnectorCategory[] = [
  'social_media',
  'storage',
  'communication',
  'crm',
  'ads',
  'analytics',
  'payments',
  'other',
];

/** Um conector é "de API" se suporta api_key ou oauth2; "de MCP" se suporta mcp. */
function matchesType(modes: ConnectorAuthMode[], filter: TypeFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'mcp') return modes.includes('mcp');
  // 'api' = qualquer modo baseado em API (api_key ou oauth2).
  return modes.includes('api_key') || modes.includes('oauth2');
}

/**
 * Busca full-text client-side sobre nome + descrição + categoria (label).
 * Case-insensitive, tokenizada — todos os termos precisam bater (AND).
 */
function matchesSearch(item: ConnectorCatalogItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item.name,
    item.description ?? '',
    item.category,
    CATEGORY_LABELS[item.category] ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
}

// ─── Página ─────────────────────────────────────────────────────────────────

export function ConnectorsPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Retorno do callback OAuth central (?connector=connected|error&reason=&slug=).
  useEffect(() => {
    const status = searchParams.get('connector');
    if (!status) return;
    if (status === 'connected') {
      const connSlug = searchParams.get('slug');
      toast({ message: `Conector ${connSlug ?? ''} conectado com sucesso.`, type: 'success' });
    } else if (status === 'error') {
      const reason = searchParams.get('reason') ?? 'erro desconhecido';
      toast({ message: `Falha ao conectar: ${reason}`, type: 'error' });
    }
    // Limpa os params pra não re-disparar o toast em re-render/refresh.
    const next = new URLSearchParams(searchParams);
    next.delete('connector');
    next.delete('reason');
    next.delete('slug');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [catalog, setCatalog] = useState<ConnectorCatalogItem[]>([]);
  const [connections, setConnections] = useState<ConnectorConnection[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);

  // filtros da grade
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<ConnectorCategory | 'all'>('all');

  // modal de conexão
  const [connectFor, setConnectFor] = useState<ConnectorCatalogItem | null>(null);
  // DEM-2128: redirect URI do callback central do Core (por instalação), exibida
  // no modal BYO pro usuário copiar pro console do app dele.
  const [redirectUri, setRedirectUri] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, conns, biz] = await Promise.all([
        api.getConnectorCatalog(),
        api.listConnectorConnections(),
        api.listBusinesses().catch(() => [] as Business[]),
      ]);
      setCatalog(cat.connectors);
      setRedirectUri(cat.redirect_uri);
      setConnections(conns);
      setBusinesses(biz);
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'Erro ao carregar conectores.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Contador de conexões (não-revogadas) por connector_slug. */
  const countBySlug = useMemo(() => {
    const map = new Map<string, number>();
    for (const conn of connections) {
      if (conn.status === 'revoked') continue;
      map.set(conn.connector_slug, (map.get(conn.connector_slug) ?? 0) + 1);
    }
    return map;
  }, [connections]);

  const filtered = useMemo(() => {
    return catalog
      .filter((c) => matchesType(c.supported_modes, typeFilter))
      .filter((c) => categoryFilter === 'all' || c.category === categoryFilter)
      .filter((c) => matchesSearch(c, search));
  }, [catalog, typeFilter, categoryFilter, search]);

  // Categorias presentes no catálogo (pra não mostrar filtro vazio).
  const availableCategories = useMemo(() => {
    const present = new Set(catalog.map((c) => c.category));
    return CATEGORY_ORDER.filter((cat) => present.has(cat));
  }, [catalog]);

  const selectedConnector = slug ? catalog.find((c) => c.slug === slug) ?? null : null;

  // ─── Detalhe do conector (rota /:slug) ────────────────────────────────────
  if (slug) {
    return (
      <ConnectorDetail
        slug={slug}
        connector={selectedConnector}
        loading={loading}
        businesses={businesses}
        connections={connections.filter((c) => c.connector_slug === slug)}
        onBack={() => navigate('/settings/connectors')}
        onChanged={load}
        connectFor={connectFor}
        setConnectFor={setConnectFor}
        redirectUri={redirectUri}
      />
    );
  }

  // ─── Grade do marketplace ─────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Conectores"
        description="Marketplace de integrações do seu RAIOS. Conecte redes, storage e serviços — no nível da conta ou atrelados a um negócio."
      />

      {/* Busca + filtros */}
      <div className="mt-4 flex flex-col gap-3">
        <div className="relative max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-raios-muted pointer-events-none"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar conector (nome, descrição, categoria)…"
            aria-label="Buscar conector"
            className="w-full bg-raios-card border border-raios-border rounded-md text-sm text-raios-text pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-raios-accent/40"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Filtro por TIPO */}
          <div className="flex items-center gap-1" role="group" aria-label="Filtrar por tipo">
            {([
              { value: 'all', label: 'Todos' },
              { value: 'api', label: 'API' },
              { value: 'mcp', label: 'MCP' },
            ] as { value: TypeFilter; label: string }[]).map((opt) => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={typeFilter === opt.value}
                onClick={() => setTypeFilter(opt.value)}
                className={
                  'text-xs px-3 py-1.5 rounded-md border transition-colors ' +
                  (typeFilter === opt.value
                    ? 'bg-raios-accent/15 text-raios-accent-text border-raios-accent/40'
                    : 'bg-raios-card text-raios-muted border-raios-border hover:text-raios-text')
                }
              >
                {opt.label}
              </button>
            ))}
          </div>

          <span className="w-px h-5 bg-raios-border mx-1" aria-hidden />

          {/* Filtro por CATEGORIA */}
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filtrar por categoria">
            <button
              type="button"
              aria-pressed={categoryFilter === 'all'}
              onClick={() => setCategoryFilter('all')}
              className={
                'text-xs px-3 py-1.5 rounded-md border transition-colors ' +
                (categoryFilter === 'all'
                  ? 'bg-raios-accent/15 text-raios-accent-text border-raios-accent/40'
                  : 'bg-raios-card text-raios-muted border-raios-border hover:text-raios-text')
              }
            >
              Todas
            </button>
            {availableCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                aria-pressed={categoryFilter === cat}
                onClick={() => setCategoryFilter(cat)}
                className={
                  'text-xs px-3 py-1.5 rounded-md border transition-colors ' +
                  (categoryFilter === cat
                    ? 'bg-raios-accent/15 text-raios-accent-text border-raios-accent/40'
                    : 'bg-raios-card text-raios-muted border-raios-border hover:text-raios-text')
                }
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grade */}
      <div className="mt-5">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-6">
            <EmptyState
              icon={Blocks}
              title="Nenhum conector encontrado"
              description={
                catalog.length === 0
                  ? 'O catálogo de conectores está vazio.'
                  : 'Nenhum conector bate com a busca e os filtros atuais.'
              }
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item) => (
              <ConnectorCard
                key={item.slug}
                item={item}
                connectionCount={countBySlug.get(item.slug) ?? 0}
                onOpen={() => navigate(`/settings/connectors/${item.slug}`)}
                onConnect={() => setConnectFor(item)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal de conexão (a partir do card) */}
      {connectFor && (
        <ConnectModal
          connector={connectFor}
          businesses={businesses}
          redirectUri={redirectUri}
          onClose={() => setConnectFor(null)}
          onConnected={async () => {
            setConnectFor(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

// ─── Card do conector ─────────────────────────────────────────────────────────

function ConnectorCard({
  item,
  connectionCount,
  onOpen,
  onConnect,
}: {
  item: ConnectorCatalogItem;
  connectionCount: number;
  onOpen: () => void;
  onConnect: () => void;
}) {
  const status = CATALOG_STATUS[item.status] ?? CATALOG_STATUS.available;
  const canConnect =
    item.status !== 'coming_soon' &&
    item.status !== 'deprecated' &&
    item.supported_modes.some((m) => CONNECTABLE_MODES.includes(m));

  return (
    <Card className="p-4 flex flex-col gap-3 h-full">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-raios-elevated/40 border border-raios-border flex items-center justify-center overflow-hidden shrink-0">
          {item.logo_url ? (
            <img
              src={item.logo_url}
              alt=""
              aria-hidden
              className="h-6 w-6 object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <Blocks className="h-5 w-5 text-raios-muted" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onOpen}
              className="text-sm font-semibold text-raios-text hover:text-raios-accent-text truncate text-left"
            >
              {item.name}
            </button>
            <Badge variant={status.variant} className="text-[10px] shrink-0">
              {status.label}
            </Badge>
          </div>
          <p className="text-xs text-raios-muted mt-1 line-clamp-2">{item.description}</p>
        </div>
      </div>

      {/* Badges de modos suportados */}
      <div className="flex flex-wrap gap-1.5">
        {item.supported_modes.map((mode) => (
          <Badge key={mode} variant="outline" className="text-[10px]">
            {MODE_LABELS[mode]}
          </Badge>
        ))}
        <Badge variant="default" className="text-[10px]">
          {CATEGORY_LABELS[item.category]}
        </Badge>
        {isReadOnly(item) && (
          <Badge variant="default" className="text-[10px] inline-flex items-center gap-1">
            <Eye className="h-3 w-3" aria-hidden /> Read-only
          </Badge>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between gap-2 pt-1">
        {/* Contador informativo (sem clique — a navegação vive no botão Gerenciar contas). */}
        <span className="text-xs text-raios-muted">
          {connectionCount > 0
            ? `${connectionCount} ${connectionCount === 1 ? 'conectada' : 'conectadas'}`
            : 'Nenhuma conexão'}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {connectionCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpen}
              title="Ver e gerenciar as contas conectadas neste conector"
            >
              <Users className="h-3.5 w-3.5" />
              Gerenciar contas
            </Button>
          )}
          <Button
            variant={connectionCount > 0 ? 'outline' : 'brand'}
            size="sm"
            onClick={onConnect}
            disabled={!canConnect}
            title={canConnect ? undefined : 'Conexão indisponível nesta fase'}
          >
            <Plug className="h-3.5 w-3.5" />
            {connectionCount > 0 ? 'Nova conexão' : 'Conectar'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ─── Detalhe do conector ──────────────────────────────────────────────────────

function ConnectorDetail({
  slug,
  connector,
  loading,
  businesses,
  connections,
  onBack,
  onChanged,
  connectFor,
  setConnectFor,
  redirectUri,
}: {
  slug: string;
  connector: ConnectorCatalogItem | null;
  loading: boolean;
  businesses: Business[];
  connections: ConnectorConnection[];
  onBack: () => void;
  onChanged: () => Promise<void> | void;
  connectFor: ConnectorCatalogItem | null;
  setConnectFor: (c: ConnectorCatalogItem | null) => void;
  /** DEM-2128: redirect URI do callback central do Core, repassada ao modal BYO. */
  redirectUri?: string;
}) {
  const { toast } = useToast();
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ConnectorConnection | null>(null);
  // Reconexão OAuth de uma conexão específica (AC3): guarda o id pra passar ao modal.
  const [reconnectFor, setReconnectFor] = useState<ConnectorConnection | null>(null);
  const readOnly = connector ? isReadOnly(connector) : false;
  const supportsOauth = connector?.supported_modes.includes('oauth2') ?? false;
  // Conector cujo modo é MCP faz re-autenticação pelo MESMO fluxo de authorize
  // (o backend já aceita reconnect_connection_id nos dois ramos). Sem isto, um
  // conector MCP-only (Apollo: supported_modes = ['mcp','api_key']) cairia numa
  // DUPLA exclusão — supportsOauth false E auth_mode !== 'oauth2' — e o botão
  // Reconectar abriria o modal de conexão NOVA em vez de re-autenticar a atual.
  const supportsMcp = connector?.supported_modes.includes('mcp') ?? false;

  const businessName = useCallback(
    (bizId: string | null) => {
      if (!bizId) return null;
      return businesses.find((b) => b.id === bizId)?.name ?? 'Negócio';
    },
    [businesses],
  );

  async function handleRevoke(conn: ConnectorConnection) {
    setRevokingId(conn.id);
    setConfirmRevoke(null);
    try {
      await api.revokeConnectorConnection(conn.id);
      toast({ message: `Conexão "${conn.display_name}" revogada.`, type: 'success' });
      await onChanged();
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'Erro ao revogar conexão.',
        type: 'error',
      });
    } finally {
      setRevokingId(null);
    }
  }

  if (loading) {
    return (
      <div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-raios-muted hover:text-raios-text mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Conectores
        </button>
        <Skeleton className="h-24 rounded-xl mb-4" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  if (!connector) {
    return (
      <div>
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-raios-muted hover:text-raios-text mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Conectores
        </button>
        <Card className="p-6">
          <EmptyState
            icon={Blocks}
            title="Conector não encontrado"
            description={`Nenhum conector com slug "${slug}" no catálogo.`}
          />
        </Card>
      </div>
    );
  }

  const status = CATALOG_STATUS[connector.status] ?? CATALOG_STATUS.available;
  const active = connections.filter((c) => c.status !== 'revoked');
  const canConnect =
    connector.status !== 'coming_soon' &&
    connector.status !== 'deprecated' &&
    connector.supported_modes.some((m) => CONNECTABLE_MODES.includes(m));

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-raios-muted hover:text-raios-text mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Conectores
      </button>

      {/* Cabeçalho do conector */}
      <Card className="p-5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-lg bg-raios-elevated/40 border border-raios-border flex items-center justify-center overflow-hidden shrink-0">
            {connector.logo_url ? (
              <img
                src={connector.logo_url}
                alt=""
                aria-hidden
                className="h-7 w-7 object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <Blocks className="h-6 w-6 text-raios-muted" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-raios-text">{connector.name}</h1>
              <Badge variant={status.variant} className="text-[10px]">
                {status.label}
              </Badge>
              {connector.supported_modes.map((mode) => (
                <Badge key={mode} variant="outline" className="text-[10px]">
                  {MODE_LABELS[mode]}
                </Badge>
              ))}
              <Badge variant="default" className="text-[10px]">
                {CATEGORY_LABELS[connector.category]}
              </Badge>
              {readOnly && (
                <Badge variant="default" className="text-[10px] inline-flex items-center gap-1">
                  <Eye className="h-3 w-3" aria-hidden /> Somente leitura
                </Badge>
              )}
            </div>
            <p className="text-sm text-raios-muted mt-1.5">{connector.description}</p>
            {connector.docs_url && (
              <a
                href={connector.docs_url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-xs text-raios-accent-text hover:underline mt-2"
              >
                <Link2 className="h-3 w-3" /> Documentação
              </a>
            )}
          </div>
          <Button
            variant="brand"
            size="sm"
            onClick={() => setConnectFor(connector)}
            disabled={!canConnect}
            title={canConnect ? undefined : 'Conexão indisponível nesta fase'}
          >
            <Plug className="h-3.5 w-3.5" /> Nova conexão
          </Button>
        </div>
      </Card>

      {/* Conexões do tenant nesse conector */}
      <div className="mt-5">
        <h2 className="text-sm font-semibold text-raios-text mb-3">
          Conexões neste tenant{' '}
          <span className="text-raios-muted font-normal">({active.length})</span>
        </h2>

        {active.length === 0 ? (
          <Card className="p-6">
            <EmptyState
              icon={Plug}
              title="Nenhuma conexão ativa"
              description="Conecte este conector à sua conta ou a um negócio."
              action={
                canConnect
                  ? { label: 'Conectar', onClick: () => setConnectFor(connector) }
                  : undefined
              }
            />
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {active.map((conn) => {
              const cs = CONN_STATUS[conn.status] ?? CONN_STATUS.connected;
              const bizName = businessName(conn.business_id);
              const ext = readExternalAccount(conn.external_account);
              const budget = readXMcpBudget(conn.external_account);
              const isExpired = conn.status === 'expired';
              // CTA de re-autenticação: vale pro modo OAuth2 E pro modo MCP (que
              // re-autentica pelo mesmo authorize, com OAuth por dentro).
              const canReconnectOauth =
                (supportsOauth && conn.auth_mode === 'oauth2') ||
                (supportsMcp && conn.auth_mode === 'mcp');
              return (
                <Card key={conn.id} className="p-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Avatar do provider (external_account.avatar — AC2). */}
                    {ext.avatar && (
                      <img
                        src={ext.avatar}
                        alt=""
                        aria-hidden
                        className="h-9 w-9 rounded-full object-cover border border-raios-border shrink-0"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-raios-text truncate">
                          {conn.display_name}
                        </span>
                        <Badge variant={cs.variant} className="text-[10px]">
                          {cs.label}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">
                          {MODE_LABELS[conn.auth_mode]}
                        </Badge>
                        {readOnly && (
                          <Badge variant="default" className="text-[10px] inline-flex items-center gap-1">
                            <Eye className="h-3 w-3" aria-hidden /> Somente leitura
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-raios-muted flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Link2 className="h-3 w-3" aria-hidden />
                          {bizName ? (
                            <>Negócio: <b className="text-raios-text-secondary">{bizName}</b></>
                          ) : (
                            <>Vínculo: <b className="text-raios-text-secondary">Conta do tenant</b></>
                          )}
                        </span>
                        {/* Handle + link do perfil (external_account — AC2). */}
                        {ext.handle && (
                          ext.profileUrl ? (
                            <a
                              href={ext.profileUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex items-center gap-1 text-raios-accent-text hover:underline"
                            >
                              {ext.handle} <ExternalLink className="h-3 w-3" aria-hidden />
                            </a>
                          ) : (
                            <span>
                              conta <b className="text-raios-text-secondary">{ext.handle}</b>
                            </span>
                          )
                        )}
                        {/* Conexão MCP sem identidade: alguns servidores MCP não
                            expõem perfil da conta no handshake (a Apollo é um
                            deles). Dizer isso é melhor do que deixar o campo
                            sumir e parecer conexão pela metade. */}
                        {!ext.handle && conn.auth_mode === 'mcp' && (
                          <span className="italic">conta não exposta pelo MCP</span>
                        )}
                        {conn.last_verified_at && (
                          <span>
                            verificada{' '}
                            <b className="text-raios-text-secondary">
                              {new Date(conn.last_verified_at).toLocaleString('pt-BR')}
                            </b>
                          </span>
                        )}
                      </div>

                      {/* Budget do X-MCP (AC4). */}
                      {budget && (
                        <div className="mt-2 text-xs text-raios-muted">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3 w-3" aria-hidden />
                            <span>
                              Uso: <b className="text-raios-text-secondary">${budget.spentUsd.toFixed(2)}</b> de{' '}
                              <b className="text-raios-text-secondary">${budget.budgetUsd.toFixed(2)}</b>{' '}
                              ({budget.calls} chamadas)
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 w-full max-w-xs rounded-full bg-raios-border overflow-hidden">
                            <div
                              className={
                                'h-full rounded-full ' +
                                (budget.pctUsed >= 80 ? 'bg-raios-danger' : 'bg-raios-accent')
                              }
                              style={{ width: `${budget.pctUsed}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Status detail + CTA de reconexão quando expirado (AC3). */}
                      {isExpired && (
                        <div className="mt-2 flex items-start gap-1.5 text-xs text-raios-warning-text">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" aria-hidden />
                          <span>
                            Conexão expirada
                            {conn.status_detail ? ` (${conn.status_detail})` : ''}. Reconecte para
                            restabelecer o acesso.
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant={isExpired ? 'brand' : 'outline'}
                        size="sm"
                        onClick={() =>
                          canReconnectOauth ? setReconnectFor(conn) : setConnectFor(connector)
                        }
                        disabled={!canConnect}
                        title="Reconectar (re-autenticar)"
                      >
                        <RefreshCw className="h-3.5 w-3.5" /> Reconectar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-raios-danger-text"
                        onClick={() => setConfirmRevoke(conn)}
                        disabled={revokingId === conn.id}
                        title="Revogar"
                      >
                        {revokingId === conn.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                        Revogar
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de conexão nova */}
      {connectFor && (
        <ConnectModal
          connector={connectFor}
          businesses={businesses}
          redirectUri={redirectUri}
          onClose={() => setConnectFor(null)}
          onConnected={async () => {
            setConnectFor(null);
            await onChanged();
          }}
        />
      )}

      {/* Modal de RECONEXÃO OAuth de uma conexão específica (AC3) */}
      {reconnectFor && connector && (
        <ConnectModal
          connector={connector}
          businesses={businesses}
          redirectUri={redirectUri}
          reconnectConnectionId={reconnectFor.id}
          onClose={() => setReconnectFor(null)}
          onConnected={async () => {
            setReconnectFor(null);
            await onChanged();
          }}
        />
      )}

      {/* Confirmação de revogação */}
      {confirmRevoke && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-raios-text">Revogar conexão</h3>
            <p className="text-sm text-raios-text-secondary">
              Revogar <strong>{confirmRevoke.display_name}</strong>? A credencial é apagada do
              vault e o acesso é encerrado imediatamente.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
                Cancelar
              </Button>
              <Button
                variant="outline"
                className="text-raios-danger-text"
                onClick={() => handleRevoke(confirmRevoke)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Revogar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
